<template>
  <div>
    <label v-if="label" for="inputId" aria-label="Select something" class="form-label">
      {{ label }}
      <span v-if="optionalLabel" class="optional-label">({{ optionalLabel }})</span>
    </label>
    <div
      :class="[
        `form-input`,
        showVanityStyle && `form-input--vanity`,
        error && `form-input--error`,
      ]"
    >
      <select
        id="selectInput"
        v-bind="$attrs"
        ref="inputEl"
        :name="selectName"
        :class="[
          `form-input__field`,
          hover && `hover`,
          focus && `focus`, 
        ]"
        :disabled="disabled"
        :multiple="multiple"
        :value="value"
        @change="change"
      >
        <option value>Select option...</option>
        <template v-for="option in options">
          <optgroup
            v-if="option.options && option.options.length > 0"
            :key="`${option.value}-${option.label}`"
            :label="option.label"
          >
            <option
              v-for="innerOption in option.options"
              :key="innerOption.value"
              :value="innerOption.value"
              :disabled="innerOption.disabled"
              :selected="selectedOption(innerOption)"
            >{{ innerOption.label }}</option>
          </optgroup>
          <option
            v-else
            :key="`${option.value}-${option.label}`"
            :value="option.value"
            :disabled="option.disabled"
            :selected="selectedOption(option)"
          >{{ option.label }}</option>
        </template>
      </select>
      <icon
        v-if="!multiple"
        class-name="form-input__icon form-input__icon--select"
        name="chevron-down"
      />
    </div>
    <p
      v-if="errorMessage"
      class="form-label form-label--error form-label--error--active"
    >{{ errorMessage }}</p>
    <div
      v-if="helpMessage"
      :class="[`form-help`, helpMessagePosition === `right` && `text-right`]"
    >{{ helpMessage }}</div>
  </div>
</template>

<script>
// Components
import Icon from "../Icon/Icon.vue";

export default {
  name: "Select",
  components: {
    Icon,
  },
  props: {
    /**
     * @description
     * Provides the list of Select Options for Select Input
     */
    options: {
      type: Array,
      required: true,
    },
    /**
     * @description
     * Provides Label for Select Input
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Placeholder for Select Input
     */
    selectName: {
      type: String,
      default: "select-input",
    },
    /**
     * @description
     * Provides Optional Label for Select Input
     */
    optionalLabel: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Error Message for Select Input
     */
    errorMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Help Message for Select Input
     */
    helpMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Checks the Select Input help message position
     * Maersk Design System supports left and right
     */
    helpMessagePosition: {
      type: String,
      validator: (val) => ["left", "right"].includes(val),
      default: "left",
    },
    /**
     * @description
     * Emits the value change on input, Parent Component needs to sync values emitted
     */
    value: {
      type: String,
      required: true,
    },
    /**
     * @description
     * Provides Type of Select Input
     * Maersk Design System supports Default & Multiple Select
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should be of Vanity Style
     */
    showVanityStyle: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should be Disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should be Hoverable
     */
    hover: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should be Focusable
     */
    focus: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should display Error Outline
     */
    error: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: null,
      localValue: this.value,
    };
  },
  watch: {
    value() {
      this.localValue = this.value;
    },
  },
  methods: {
    selectedOption(option) {
      if (this.localValue && this.localValue.value) {
        return option.value === this.localValue.value;
      }
      return false;
    },
    change(e) {
      if (!(e && this.options && this.options.length > 0)) {
        return;
      }
      const selectedItem = e.target.value;
      let selectedOption;
      // Loop through the layers and get the first match for the selected item.
      for (const option of this.options) {
        if (option.options && Array.isArray(option.options)) {
          selectedOption = option.options.find(
            (innerOption) => selectedItem == innerOption.value
          );
          if (selectedOption) {
            // selectedOption = io;
            break;
          }
        } else {
          if (selectedItem == option.value) {
            selectedOption = option;
            break;
          }
        }
      }
      this.localValue = selectedOption;
      this.$emit("input", this.localValue);
    },
  },
};
</script>

<style lang="scss" scoped>
.form-input__field {
  cursor: pointer;

  &:disabled {
      cursor: not-allowed;
    }

  option {
    &:disabled {
      cursor: not-allowed;
      background-color: var(--Gray-100);
    }
  }
}
</style>
